import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { PURCHASES_BANNER_TYPE } from '../../const';
import CatalogApi from '../../service/Api/catalogApi';

const initialState = {
    showSidebar: false,
    advisors: {
        advisorsList: [],
        // Alias-keyed cache that accumulates advisor docs across every
        // successful fetch (never replaced). Used by the sidebar's
        // useAdvisorList so category filtering on the catalog grid —
        // which replaces `advisorsList` with the filtered subset — can't
        // make sidebar avatars disappear (HORO-10396).
        advisorsByAlias: {},
        loading: true,
        error: null,
        currentPage: 1,
        success: true,
        reloading: false,
        hasMore: true,
        currentRequestId: null,
        totalItems: 0
    },
    categories: {
        categoriesList: [],
        activeCategoriesFilters: [],
        loading: true,
        error: null,
        success: true
    },
    purchasesBannerData: {
        title: PURCHASES_BANNER_TYPE['0']
    },
    singlePageAdvisorDetails: {
        loading: true,
        data: null,
        error: null
    },
    singlePageActivityDetails: {
        loading: true,
        data: null,
        error: null
    },
    activities: {
        list: [],
        loading: true,
        error: null,
        currentPage: 1,
        hasMore: true,
        reloading: true,
        currentRequestId: null,
        totalItems: 0
    }
};

export const fetchAdvisors = createAsyncThunk('advisor/fetchAdvisors', async (params, { rejectedWithValue }) => {
    try {
        const data = await CatalogApi.getAdvisorsList(params.categories, params.page);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const fetchActivities = createAsyncThunk('advisor/fetchActivities', async (params, { rejectWithValue }) => {
    try {
        const data = await CatalogApi.getActivitiesList(params.categories, params.page);
        return data.data.data;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const fetchActivity = createAsyncThunk('advisor/fetchActivity', async (activityId, { rejectWithValue }) => {
    try {
        const data = await CatalogApi.getActivity(activityId);
        return data.data.data;
    } catch (error) {
        return rejectWithValue(error.response.status);
    }
});

export const fetchCategories = createAsyncThunk('advisor/fetchCategories', async (params, { rejectedWithValue }) => {
    try {
        const data = await CatalogApi.getAdvisorsCategories();
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const setPurchasesBannerType = createAction('catalog/setPurchasesBannerType');

export const fetchAdvisorDetailsData = createAsyncThunk('advisor/fetchAdvisorDetailsData', async (advisorAlias, { rejectWithValue }) => {
    try {
        const data = await CatalogApi.getAdvisorDetails(advisorAlias);
        return data.data;
    } catch (error) {
        return rejectWithValue(error.response.status);
    }
});

export const clearAdvisorDetailInfo = createAction('catalog/clearAdvisorDetailInfo');
export const clearActivityDetailInfo = createAction('catalog/clearActivityDetailInfo');

export const getCategoriesState = (store) => store.catalog.categories;
export const getAdvisorsState = (store) => store.catalog.advisors;
export const getAdvisorsByAlias = (store) => store.catalog.advisors.advisorsByAlias;
export const getActivitiesState = (store) => store.catalog.activities;
export const getCatalogSidebarState = (store) => store.catalog.showSidebar;
export const getPurchasesBannerData = (store) => store.catalog.purchasesBannerData;
export const getAdvisorSinglePageDetails = (store) => store.catalog.singlePageAdvisorDetails;
export const getActivitySinglePageDetails = (store) => store.catalog.singlePageActivityDetails;

// Advisors flagged as participants in the multi-advisor handoff "+" panel.
// Backend marks the advisor doc with availableForHandoff: true (similar to
// isAllowMulti). Falls back to empty list if catalog hasn't been fetched yet.
export const getHandoffAvailableAdvisors = (store) =>
    (store.catalog.advisors.advisorsList || []).filter((advisor) => advisor?.availableForHandoff === true);

const catalogSlice = createSlice({
    name: 'catalog',
    initialState,
    reducers: {
        toggleSidebar(store, { payload }) {
            store.showSidebar = payload;
        },
        clearCategoriesError(store) {
            store.error = null;
        },
        clearAdvisorError(store) {
            store.error = null;
        },
        clearFilters(store) {
            store.categories.activeCategoriesFilters = [];
        },
        addCategoryFilter(store, { payload }) {
            const reset = payload?.reset ? payload.reset : false;
            store.categories.activeCategoriesFilters = [
                ...(reset ? [] : store.categories.activeCategoriesFilters),
                ...(Array.isArray(payload.filters) ? payload.filters : [payload.filters])
            ];
        },
        removeCategoryFilter(store, { payload }) {
            store.categories.activeCategoriesFilters = store.categories.activeCategoriesFilters.filter((item) => item !== payload);
        }
    },
    extraReducers(builder) {
        builder
            .addCase(fetchAdvisors.pending, (store, { meta: { arg, requestId } }) => {
                const reloading = typeof arg?.reloading === 'undefined' ? true : arg.reloading;
                store.advisors = {
                    ...store.advisors,
                    loading: true,
                    error: null,
                    reloading,
                    currentRequestId: requestId
                };
            })
            .addCase(fetchAdvisors.fulfilled, (store, { payload, meta }) => {
                if (meta.requestId && meta.requestId !== store.advisors.currentRequestId) {
                    return;
                }
                const reloading = store.advisors.reloading;
                // Accumulate every advisor doc we see into the alias map
                // — pagination and category-filtered fetches both add
                // entries; nothing ever removes them. Sidebar reads from
                // here so category filtering on `advisorsList` can't
                // blank out avatars (HORO-10396).
                const mergedByAlias = { ...store.advisors.advisorsByAlias };
                for (const advisor of payload.data.advisors) {
                    if (advisor?.alias) mergedByAlias[advisor.alias] = advisor;
                }
                store.advisors = {
                    ...store.advisors,
                    loading: false,
                    success: payload.success,
                    advisorsList: reloading ? payload.data.advisors : [...store.advisors.advisorsList, ...payload.data.advisors],
                    advisorsByAlias: mergedByAlias,
                    hasMore: payload.data.currentPage < payload?.data?.allPages,
                    currentPage: Number.parseInt(payload?.data?.currentPage, 10) + 1,
                    totalItems: payload.data.totalItems
                };
            })
            .addCase(fetchAdvisors.rejected, (store, { payload }) => {
                store.advisors = {
                    ...store.advisors,
                    loading: false,
                    reloading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(fetchActivities.pending, (store, { meta: { arg, requestId } }) => {
                const reloading = typeof arg?.reloading === 'undefined' ? true : arg.reloading;
                store.activities = {
                    ...store.activities,
                    loading: true,
                    error: null,
                    reloading,
                    currentRequestId: requestId
                };
            })
            .addCase(fetchActivities.fulfilled, (store, { payload, meta }) => {
                if (meta.requestId && meta.requestId !== store.activities.currentRequestId) {
                    return;
                }
                const reloading = store.activities.reloading;
                store.activities = {
                    ...store.activities,
                    reloading: false,
                    loading: false,
                    list: reloading ? [...payload.activities] : [...store.activities.list, ...payload.activities],
                    hasMore: payload.currentPage < payload?.allPages,
                    currentPage: Number.parseInt(payload?.currentPage, 10) + 1,
                    totalItems: payload.totalItems
                };
            })
            .addCase(fetchActivities.rejected, (store, { payload }) => {
                store.activities = {
                    ...store.activities,
                    loading: false,
                    error: payload?.error,
                    reloading: false
                };
            })
            .addCase(fetchCategories.pending, (store) => {
                store.categories = {
                    ...store.categories,
                    loading: true,
                    error: null
                };
            })
            .addCase(fetchCategories.fulfilled, (store, { payload }) => {
                store.categories = {
                    ...store.categories,
                    loading: false,
                    success: payload.success,
                    categoriesList: payload.categories
                };
            })
            .addCase(fetchCategories.rejected, (store, { payload }) => {
                store.categories = {
                    ...store.categories,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(setPurchasesBannerType, (store, { payload }) => {
                store.purchasesBannerData.title = payload;
            })
            .addCase(fetchAdvisorDetailsData.pending, (store) => {
                store.singlePageAdvisorDetails.loading = true;
                store.singlePageAdvisorDetails.error = null;
            })
            .addCase(fetchAdvisorDetailsData.fulfilled, (store, { payload }) => {
                store.singlePageAdvisorDetails = {
                    loading: false,
                    data: payload.landing
                };
            })
            .addCase(fetchAdvisorDetailsData.rejected, (store, { payload }) => {
                store.singlePageAdvisorDetails.loading = false;
                store.singlePageAdvisorDetails.error = payload;
            })
            .addCase(fetchActivity.pending, (store) => {
                store.singlePageActivityDetails.loading = true;
                store.singlePageActivityDetails.error = null;
            })
            .addCase(fetchActivity.fulfilled, (store, { payload }) => {
                store.singlePageActivityDetails = {
                    loading: false,
                    data: payload
                };
            })
            .addCase(fetchActivity.rejected, (store, { payload }) => {
                store.singlePageActivityDetails.loading = false;
                store.singlePageActivityDetails.error = payload;
            })
            .addCase(clearAdvisorDetailInfo, (store) => {
                store.singlePageAdvisorDetails.error = null;
            })
            .addCase(clearActivityDetailInfo, (store) => {
                store.singlePageAdvisorDetails.error = null;
            });
    }
});

export const catalogReducer = catalogSlice.reducer;

export const { toggleSidebar, addCategoryFilter, removeCategoryFilter, clearCategoriesError, clearAdvisorError, clearFilters } =
    catalogSlice.actions;
